import React from "react";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  Icon,
} from "@mui/material";
import { TableColumn } from "../../../../app.interface";
import { IdDisplay } from "../../components/IdDisplay";
import { DateDisplay } from "../../components/DateDisplay";
import { hasPermission } from "../../../auth/utils/permission.utils";
import { toLower } from "lodash";

interface JournalColumnsProps {
  onJournalClick: (journal: any) => void;
  onReverseJournal: (journal: any) => void;
  onDeleteJournal: (journal: any) => void;
  onEditJournal: (journal: any) => void;
  onPostJournal: (journal: any) => void;
  onCopyJournalId: (journalId: string) => void;
  user: any;
}

export const createJournalColumns = ({
  onJournalClick,
  onReverseJournal,
  onDeleteJournal,
  onEditJournal,
  onPostJournal,
  onCopyJournalId,
  user,
}: JournalColumnsProps): TableColumn[] => {
  return [
    {
      id: "journalEntryId",
      header: "Journal ID",
      field: "journalEntryId",
      type: "action",
      width: "min-content",
      renderAction: (row: any) => (
        <IdDisplay
          id={row.journal_entry_number || row.id}
          onClick={() => onJournalClick(row)}
          onCopy={onCopyJournalId}
          label="Copy Journal ID"
        />
      ),
    },
    {
      id: "entryDate",
      header: "Date",
      field: "entryDate",
      type: "action",
      width: "min-content",
      renderAction: (row: any) => (
        <Box
          sx={{
            py: 0.5,
            minHeight: 40,
            display: "flex",
            alignItems: "center",
            maxWidth: 350,
            minWidth: 90,
          }}
        >
          <DateDisplay timestamp={row.entry_date} showTooltip={true} />
        </Box>
      ),
    },
    {
      id: "narration",
      header: "Narration",
      field: "narration",
      type: "action",
      width: "min-content",
      renderAction: (row: any) => (
        <Box
          sx={{
            py: 0.5,
            minHeight: 40,
            maxWidth: 350,
            width: "fit-content",
            minWidth: 200,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              wordBreak: "break-word",
              lineHeight: 1.4,
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {row.narration}
          </Typography>
        </Box>
      ),
    },
    {
      id: "sourceModule",
      header: "Source",
      field: "sourceModule",
      type: "action",
      width: "min-content",
      renderAction: (row: any) => {
        const getSourceConfig = (source: string | undefined) => {
          if (!source) {
            return { color: "default", icon: "description" };
          }

          switch (source.toLowerCase()) {
            case "pos":
              return { color: "success", icon: "point_of_sale" };
            case "payroll":
              return { color: "info", icon: "payments" };
            case "procurement":
              return { color: "warning", icon: "shopping_cart" };
            case "manual":
              return { color: "primary", icon: "edit" };
            case "adjustment":
              return { color: "secondary", icon: "tune" };
            default:
              return { color: "default", icon: "description" };
          }
        };

        const config = getSourceConfig(row.source_module);

        return (
          <Box
            sx={{
              py: 0.5,
              minHeight: 40,
              display: "flex",
              alignItems: "center",
              maxWidth: 350,
              width: "fit-content",
            }}
          >
            <Chip
              label={row.source_module || "Unknown"}
              size="small"
              color={config.color as any}
              variant="outlined"
              icon={<Icon sx={{ fontSize: 14 }}>{config.icon}</Icon>}
              sx={{
                fontWeight: 500,
                "& .MuiChip-icon": {
                  color: "inherit",
                },
              }}
            />
          </Box>
        );
      },
    },
    {
      id: "branch",
      header: "Branch",
      field: "branch",
      type: "action",
      width: "min-content",
      renderAction: (row: any) => {
        const getBranchInfo = (
          branchId: string | undefined,
          branchName: string | undefined
        ) => {
          if (!branchId) {
            return { name: "Organization", color: "primary", icon: "business" };
          }
          return {
            name: branchName || "Unknown Branch",
            color: "info",
            icon: "store",
          };
        };

        const branchInfo = getBranchInfo(row.branch_id, row.branch_name);

        return (
          <Box
            sx={{
              py: 0.5,
              minHeight: 40,
              display: "flex",
              alignItems: "center",
              maxWidth: 350,
              width: "fit-content",
            }}
          >
            <Chip
              label={branchInfo.name}
              size="small"
              color={branchInfo.color as any}
              variant="outlined"
              icon={<Icon sx={{ fontSize: 14 }}>{branchInfo.icon}</Icon>}
              sx={{
                fontWeight: 500,
                "& .MuiChip-icon": {
                  color: "inherit",
                },
              }}
            />
          </Box>
        );
      },
    },
    {
      id: "referenceId",
      header: "Reference",
      field: "referenceId",
      type: "action",
      width: "min-content",
      renderAction: (row: any) => (
        <Box
          sx={{
            py: 0.5,
            minHeight: 40,
            display: "flex",
            alignItems: "center",
            maxWidth: 350,
            minWidth: 130,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontFamily: "monospace",
              fontSize: "0.8rem",
              color: row.reference_id ? "text.primary" : "text.disabled",
              wordBreak: "break-all",
              lineHeight: 1.4,
            }}
          >
            {row.reference_id || "—"}
          </Typography>
        </Box>
      ),
    },
    {
      id: "created_by",
      header: "Created By",
      field: "created_by_email",
      type: "action",
      width: "min-content",
      renderAction: (row: any) => (
        <Box
          sx={{
            py: 0.5,
            minHeight: 40,
            display: "flex",
            alignItems: "center",
            maxWidth: 350,
            width: "fit-content",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              lineHeight: 1.4,
            }}
          >
            {row.created_by_email || "—"}
          </Typography>
        </Box>
      ),
    },
    {
      id: "journal_type",
      header: "Journal Type",
      field: "journal_type",
      type: "action",
      width: "min-content",
      renderAction: (row: any) => {
        const journalTypeTag = row.tags?.find(
          (tag: any) => tag.tag_type === "JOURNAL_TYPE"
        );
        const journalType = journalTypeTag?.tag_value || "Unknown";

        return (
          <Box
            sx={{
              py: 0.5,
              minHeight: 40,
              display: "flex",
              alignItems: "center",
              maxWidth: 350,
              width: "fit-content",
              minWidth: 100,
            }}
          >
            <Chip
              label={journalType.charAt(0).toUpperCase() + journalType.slice(1)}
              size="small"
              variant="outlined"
              sx={{
                fontWeight: 500,
                fontSize: "0.75rem",
                textTransform: "capitalize",
              }}
            />
          </Box>
        );
      },
    },
    {
      id: "amounts",
      header: "Debit / Credit",
      field: "amounts",
      type: "action",
      width: "min-content",
      align: "center",
      renderAction: (row: any) => {
        // Calculate totals from journal_lines
        const totalDebit =
          row.journal_lines?.reduce(
            (sum: number, line: any) => sum + (line.debit || 0),
            0
          ) || 0;
        const totalCredit =
          row.journal_lines?.reduce(
            (sum: number, line: any) => sum + (line.credit || 0),
            0
          ) || 0;

        return (
          <Box
            sx={{
              py: 0.5,
              minHeight: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              maxWidth: 250,
              minWidth: 200,
              gap: 2,
            }}
          >
            {/* Debit Amount */}
            <Box sx={{ textAlign: "left", flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontFamily: "monospace",
                  color: totalDebit > 0 ? "success.main" : "text.disabled",
                  fontSize: "0.85rem",
                }}
              >
                {totalDebit > 0
                  ? `${row.currency} ${totalDebit.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                    })}`
                  : "—"}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.7rem",
                  display: "block",
                  lineHeight: 1,
                }}
              >
                Debit
              </Typography>
            </Box>

            {/* Divider */}
            <Box
              sx={{
                width: 1,
                height: 30,

                mx: 1,
              }}
            />

            {/* Credit Amount */}
            <Box sx={{ textAlign: "right", flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontFamily: "monospace",
                  color: totalCredit > 0 ? "error.main" : "text.disabled",
                  fontSize: "0.85rem",
                }}
              >
                {totalCredit > 0
                  ? `${row.currency} ${totalCredit.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                    })}`
                  : "—"}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.7rem",
                  display: "block",
                  lineHeight: 1,
                }}
              >
                Credit
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      id: "status",
      header: "Status",
      field: "status",
      type: "action",
      width: "min-content",
      renderAction: (row: any) => {
        const getStatusConfig = (status: string) => {
          switch (status.toLowerCase()) {
            case "posted":
              return { color: "success", label: "Posted" };
            case "draft":
              return { color: "warning", label: "Draft" };
            case "reversed":
              return { color: "error", label: "Reversed" };
            case "pending":
              return { color: "info", label: "Pending" };
            default:
              return { color: "default", label: status };
          }
        };

        const config = getStatusConfig(row.status);

        return (
          <Box
            sx={{
              py: 0.5,
              minHeight: 40,
              display: "flex",
              alignItems: "center",
              maxWidth: 350,
              width: "fit-content",
            }}
          >
            <Chip
              label={config.label}
              size="small"
              color={config.color as any}
              variant={config.color === "success" ? "filled" : "outlined"}
              sx={{
                fontWeight: 600,
                fontSize: "0.75rem",
              }}
            />
          </Box>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      field: "actions",
      type: "action",
      width: "min-content",
      align: "center",
      renderAction: (row: any) => (
        <Box
          sx={{
            py: 0.5,
            minHeight: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            maxWidth: 350,
            width: "fit-content",
          }}
        >
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="View Details" arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onJournalClick(row);
                }}
                sx={{
                  color: "info.main",
                  "&:hover": {
                    bgcolor: "action.hover",
                  },
                }}
              >
                <Icon sx={{ fontSize: 18 }}>visibility</Icon>
              </IconButton>
            </Tooltip>

            {/* {toLower(row.status) === "draft" && (
              <Tooltip title="Edit Draft" arrow>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditJournal(row);
                  }}
                  sx={{
                    color: "primary.main",
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  <Icon sx={{ fontSize: 18 }}>edit</Icon>
                </IconButton>
              </Tooltip>
            )} */}

            {toLower(row.status) === "draft" && (
              <Tooltip
                title={
                  !hasPermission(
                    "accounting.journals.post",
                    user?.newPermissions,
                    user?.assignment_details?.id,
                    user?.is_store_owner
                  )
                    ? "You don't have permission to post journal"
                    : "Post Draft to Ledger"
                }
                arrow
              >
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPostJournal(row);
                  }}
                  disabled={
                    !hasPermission(
                      "accounting.journals.post",
                      user?.newPermissions,
                      user?.assignment_details?.id,
                      user?.is_store_owner
                    )
                  }
                  sx={{
                    color: "success.main",
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                    "&.Mui-disabled": {
                      color: "action.disabled",
                    },
                  }}
                >
                  <Icon sx={{ fontSize: 18 }}>check_circle</Icon>
                </IconButton>
              </Tooltip>
            )}

            {toLower(row.status) === "posted" && (
              <Tooltip title="Reverse Journal" arrow>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onReverseJournal(row);
                  }}
                  sx={{
                    color: "warning.main",
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  <Icon sx={{ fontSize: 18 }}>undo</Icon>
                </IconButton>
              </Tooltip>
            )}

            {toLower(row.status) === "draft" && (
              <Tooltip
                title={
                  !hasPermission(
                    "accounting.journals.delete_draft",
                    user?.newPermissions,
                    user?.assignment_details?.id,
                    user?.is_store_owner
                  )
                    ? "You don't have permission to delete draft"
                    : "Delete Draft"
                }
                arrow
              >
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteJournal(row);
                  }}
                  disabled={
                    !hasPermission(
                      "accounting.journals.delete_draft",
                      user?.newPermissions,
                      user?.assignment_details?.id,
                      user?.is_store_owner
                    )
                  }
                  sx={{
                    color: "error.main",
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  <Icon sx={{ fontSize: 18 }}>delete</Icon>
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Box>
      ),
    },
  ];
};
